import React, { useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useForm, FormProvider } from 'react-hook-form';
import SentimentVerySatisfiedOutlined from '@mui/icons-material/SentimentVerySatisfiedOutlined';
import { Alert, Button, CircularProgress, LinearProgress } from '@mui/material';

import BackButtonLayoutTitleAction from 'retired/BackButtonLayout/BackButtonLayoutTitleAction';
import { CommunityQuery, useCommunityQuery, UserFragment } from 'generated/graphql';
import { Chip } from 'components/Chip';
import { H4 } from 'components/Typography';
import BackButtonLayout from 'retired/BackButtonLayout/BackButtonLayout';
import { selectOrganization, selectUser } from 'entities/user/selectors';

import { useRedirectIfNoCommunityAccess } from '../CommunitiesPage/hasCommunityAccess';

import { FormFieldValues } from './CommunityForm/formType';
import { CommunityForm } from './CommunityForm/CommunityForm';
import { useCommunityMutation } from './CommunityForm/useCommunityMutation';

const getLeads = (community: CommunityQuery['community']): UserFragment[] =>
  community?.leadMembers?.map((leadMember) => leadMember.User) || [];

// Forcing defaultValue to be FormFieldValues (no Partial!) so we ensure we don't miss fields as we expand the form
const getDefaultValues = (community: CommunityQuery['community']): FormFieldValues => {
  if (!community) {
    return {
      enabled: true,
      title: '',
      description: '',
      leads: [],
      headerImage: null,
      tileImage: null,
      nonprofits: [],
      allowAllyMembers: false,
      allowAnonymousMembers: false,
      isPrivate: false,
    };
  }

  return {
    // Despite setting "enabled" default value to true, Prisma doesn't return the default value unless we make the field required
    // Making it required prevents us from querying { enabled: { isSet: false } }
    // More context here: https://github.com/joindeed/marketplace/pull/1751/files#diff-700338d7fe7e786fb05d9f010e1681d2730c767d0fac114988d398672ef57af9R35-R41
    enabled: typeof community.enabled === 'boolean' ? community.enabled : true,
    title: community.title,
    description: community.description || '',
    leads: getLeads(community),
    headerImage: community.headerImage,
    tileImage: community.tileImage,
    nonprofits: community.Nonprofits || [],
    allowAllyMembers: community.allowAllyMembers ?? false,
    allowAnonymousMembers: community.allowAnonymousMembers ?? false,
    isPrivate: community.isPrivate || false,
  };
};

export const CommunityPage = (): JSX.Element => {
  const user = useSelector(selectUser);
  const organization = useSelector(selectOrganization);
  const { id: communityId } = useParams<{ id: string }>();
  const communityQuery = useCommunityQuery({
    variables: { communityId },
    skip: !communityId,
  });

  const community = communityQuery.data?.community;
  // location is used to determine if we're working with teams or communities especially when creating a new team
  const location = useLocation();
  const isTeam = community?.type === 'Team' || location.pathname.startsWith('/teams');
  const entityType = isTeam ? 'team' : 'community';
  const entityTypePlural = isTeam ? 'teams' : 'communities';
  const [communityMutation, mutationResult] = useCommunityMutation(communityId, entityType);

  const formMethods = useForm<FormFieldValues>({
    mode: 'all',
    defaultValues: getDefaultValues(community),
  });

  const hasAccess = useRedirectIfNoCommunityAccess(user, organization);
  useEffect(() => {
    formMethods.reset(getDefaultValues(community));
  }, [community, formMethods]);

  const isLoading = !hasAccess || communityQuery.loading;

  const saveButton = isLoading ? null : (
    <Button
      onClick={formMethods.handleSubmit(({ leads, nonprofits, ...formValues }: FormFieldValues) =>
        communityMutation({
          ...formValues,
          leadIds: leads.map((lead) => lead.id),
          nonprofitIds: nonprofits.map((nonprofit) => nonprofit.id),
          ...(entityType === 'team' && { type: 'Team' }),
        })
      )}
      disabled={mutationResult.loading}
      startIcon={mutationResult.loading && <CircularProgress size="1rem" />}
      variant="contained"
    >
      Save
    </Button>
  );

  const backLocation = communityId ? `/${entityTypePlural}/${communityId}` : `/${entityTypePlural}`;

  return (
    <>
      <BackButtonLayoutTitleAction
        title={
          communityId
            ? `Edit ${entityType}`
            : `Create a ${entityType === 'team' ? 'Team' : 'Community'}`
        }
        to={backLocation}
        rightActions={saveButton}
      >
        {mutationResult.error && (
          <Alert severity="error">An error occurred while saving the {entityType}</Alert>
        )}
        {isLoading ? (
          <LinearProgress />
        ) : (
          <FormProvider {...formMethods}>
            <CommunityForm entityType={entityType} />
          </FormProvider>
        )}
      </BackButtonLayoutTitleAction>
      {!isLoading && (
        <BackButtonLayout to={backLocation}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flex: '1 1 auto',
              minHeight: '100%',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Chip
                style={{ backgroundColor: '#D2FFE8', marginRight: '8px' }}
                label={<SentimentVerySatisfiedOutlined htmlColor="#23A461" />}
              />
              <H4>All Set</H4>
            </div>
            {saveButton}
          </div>
        </BackButtonLayout>
      )}
    </>
  );
};

export default CommunityPage;
